// src/components/Orders/OrderDetailsModal.js
import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/LanguageContext";

const OrderDetailsModal = ({ order, isOpen, onClose }) => {
  const { t, tStatus } = useTranslation();
  if (!isOpen || !order) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount, currency = "KWD") => {
    if (amount === null || amount === undefined) return "N/A";
    return `${Number(amount).toFixed(2)} ${currency}`;
  };

  const getStatusBadge = (status, type = "order") => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      refunded: "bg-purple-100 text-purple-800",
      created: "bg-gray-100 text-gray-800",
      picked_up: "bg-blue-100 text-blue-800",
      in_transit: "bg-indigo-100 text-indigo-800",
      delivered: "bg-green-100 text-green-800",
      returned: "bg-red-100 text-red-800",
      active: "bg-green-100 text-green-800",
      approved: "bg-green-100 text-green-800",
    };

    return statusColors[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  // Calculate total from invoice items
  const calculateTotal = () => {
    if (!order.invoiceItems || order.invoiceItems.length === 0) return 0;
    return order.invoiceItems.reduce((acc, item) => acc + (item.unitPrice * item.quantity || 0), 0);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-dashboardDark px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-semibold text-white">{t("orderModal.detailsTitle")}</h2>
              <p className="text-white/70 text-sm">{t("orderModal.orderNumber")}{order.customOrderId || order._id}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/10 rounded-full p-1 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Order Status Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-dashboardBg rounded-lg p-4">
                <p className="text-dashboardText text-sm mb-1">{t("orderModal.orderStatus")}</p>
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(order.orderStatus)}`}>
                  {tStatus(order.orderStatus)}
                </span>
              </div>
              <div className="bg-dashboardBg rounded-lg p-4">
                <p className="text-dashboardText text-sm mb-1">{t("orderModal.deliveryStatus")}</p>
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(order.deliveryStatus)}`}>
                  {tStatus(order.deliveryStatus)}
                </span>
              </div>
              <div className="bg-dashboardBg rounded-lg p-4">
                <p className="text-dashboardText text-sm mb-1">{t("orderModal.invoiceValue")}</p>
                <p className="text-dashboardDarkText font-semibold text-lg">
                  {formatCurrency(order.invoiceValue, order.displayCurrencyIso)}
                </p>
              </div>
            </div>

            {/* Customer Information */}
            <div className="border border-dashboardStroke rounded-lg overflow-hidden">
              <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.customerInfo")}</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  {order.userId?.profilePicture && (
                    <img
                      src={order.userId.profilePicture}
                      alt={order.userId?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="text-dashboardText text-sm">{t("orderModal.customerName")}</p>
                    <p className="text-dashboardDarkText font-medium">{order.customerName || order.userId?.name || "N/A"}</p>
                  </div>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.email")}</p>
                  <p className="text-dashboardDarkText font-medium">{order.customerEmail || order.userId?.email || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.mobile")}</p>
                  <p className="text-dashboardDarkText font-medium">
                    {order.mobileCountryCode && order.customerMobile
                      ? `${order.mobileCountryCode} ${order.customerMobile}`
                      : order.mobileCountryCode || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.userHandle")}</p>
                  <p className="text-dashboardDarkText font-medium">@{order.userId?.userHandle || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.accountStatus")}</p>
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(order.userId?.accountStatus)}`}>
                    {tStatus(order.userId?.accountStatus)}
                  </span>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.walletBalance")}</p>
                  <p className="text-dashboardDarkText font-medium">{formatCurrency(order.userId?.walletBalance)}</p>
                </div>
              </div>
            </div>

            {/* Seller Information */}
            {order.sellerId && (
              <div className="border border-dashboardStroke rounded-lg overflow-hidden">
                <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                  <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.sellerInfo")}</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    {order.sellerId?.profilePicture && (
                      <img
                        src={order.sellerId.profilePicture}
                        alt={order.sellerId?.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.sellerName")}</p>
                      <p className="text-dashboardDarkText font-medium">{order.sellerId?.name || "N/A"}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-dashboardText text-sm">{t("orderModal.email")}</p>
                    <p className="text-dashboardDarkText font-medium">{order.sellerId?.email || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-dashboardText text-sm">{t("orderModal.userHandle")}</p>
                    <p className="text-dashboardDarkText font-medium">@{order.sellerId?.userHandle || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-dashboardText text-sm">{t("orderModal.verificationStatus")}</p>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(order.sellerId?.identityVerification?.status)}`}>
                      {order.sellerId?.identityVerification?.status ? tStatus(order.sellerId.identityVerification.status) : tStatus("notVerified")}
                    </span>
                  </div>
                  {order.sellerId?.identityVerification?.company && (
                    <>
                      <div>
                        <p className="text-dashboardText text-sm">{t("orderModal.companyName")}</p>
                        <p className="text-dashboardDarkText font-medium">
                          {order.sellerId?.identityVerification?.company?.companyName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-dashboardText text-sm">{t("orderModal.companyPhone")}</p>
                        <p className="text-dashboardDarkText font-medium">
                          {order.sellerId?.identityVerification?.company?.companyPhoneNumber || "N/A"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Invoice Items */}
            <div className="border border-dashboardStroke rounded-lg overflow-hidden">
              <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.invoiceItems")}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-dashboardStroke">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("orderModal.itemName")}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("orderModal.moduleType")}</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-dashboardText">{t("orderModal.quantity")}</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-dashboardText">{t("orderModal.unitPrice")}</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-dashboardText">{t("orderModal.total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.invoiceItems?.map((item, index) => (
                      <tr key={index} className="border-b border-dashboardStroke last:border-b-0">
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{item.itemName || "N/A"}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText capitalize">{item.moduleType || "N/A"}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText text-center">{item.quantity || 0}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText text-right">
                          {formatCurrency(item.unitPrice, order.displayCurrencyIso)}
                        </td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText font-medium text-right">
                          {formatCurrency((item.unitPrice || 0) * (item.quantity || 0), order.displayCurrencyIso)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                        {t("orderModal.subtotal")}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                        {formatCurrency(calculateTotal(), order.displayCurrencyIso)}
                      </td>
                    </tr>
                    {order.individualShippingFee > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                          {t("orderModal.shippingFee")}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                          {formatCurrency(order.individualShippingFee, order.displayCurrencyIso)}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                        {t("orderModal.walletCredit")}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-dashboardDarkText text-right">
                        {(order.paidWithWalletAmount || 0) > 0
                          ? `-${formatCurrency(order.paidWithWalletAmount, order.displayCurrencyIso)}`
                          : formatCurrency(0, order.displayCurrencyIso)}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-green-600 text-right">
                        {t("orderModal.discount")}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right">
                        {(order.appliedDiscount || 0) > 0
                          ? `-${formatCurrency(order.appliedDiscount, order.displayCurrencyIso)}`
                          : formatCurrency(0, order.displayCurrencyIso)}
                      </td>
                    </tr>
                    <tr className="bg-dashboardBg">
                      <td colSpan="4" className="px-4 py-3 text-base font-bold text-dashboardDarkText text-right">
                        {t("orderModal.totalLabel")}
                      </td>
                      <td className="px-4 py-3 text-base font-bold text-dashboardCoral text-right">
                        {formatCurrency(order.invoiceValue, order.displayCurrencyIso)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Shipping Method */}
              {order.shippingMethod && (
                <div className="border border-dashboardStroke rounded-lg overflow-hidden">
                  <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                    <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.shippingMethod")}</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.provider")}</p>
                      <p className="text-dashboardDarkText font-medium">{order.shippingMethod?.title || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.contactPerson")}</p>
                      <p className="text-dashboardDarkText font-medium">{order.shippingMethod?.contactPerson || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.contactNumber")}</p>
                      <p className="text-dashboardDarkText font-medium">{order.shippingMethod?.contactNumber || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.email")}</p>
                      <p className="text-dashboardDarkText font-medium">{order.shippingMethod?.email || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.shippingCharges")}</p>
                      <p className="text-dashboardDarkText font-medium">
                        {formatCurrency(order.shippingMethod?.shippingCharges, order.displayCurrencyIso)}
                      </p>
                    </div>
                    <div>
                      <p className="text-dashboardText text-sm">{t("orderModal.servicesOffered")}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {order.shippingMethod?.servicesOffered?.map((service, index) => (
                          <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Address */}
              <div className="border border-dashboardStroke rounded-lg overflow-hidden">
                <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                  <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.customerAddress")}</h3>
                </div>
                <div className="p-4 space-y-3">
                  {order.customerAddress ? (
                    <>
                      <div>
                        <p className="text-dashboardText text-sm">{t("orderModal.addressLine1")}</p>
                        <p className="text-dashboardDarkText font-medium">
                          {order.customerAddress?.addressLine1 || "N/A"}
                        </p>
                      </div>
                      {order.customerAddress?.addressLine2 && (
                        <div>
                          <p className="text-dashboardText text-sm">{t("orderModal.addressLine2")}</p>
                          <p className="text-dashboardDarkText font-medium">
                            {order.customerAddress.addressLine2}
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-dashboardText text-sm">{t("orderModal.city")}</p>
                          <p className="text-dashboardDarkText font-medium">
                            {order.customerAddress?.city || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-dashboardText text-sm">{t("orderModal.state")}</p>
                          <p className="text-dashboardDarkText font-medium">
                            {order.customerAddress?.state || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-dashboardText text-sm">{t("orderModal.zipCode")}</p>
                        <p className="text-dashboardDarkText font-medium">
                          {order.customerAddress?.zipCode || "N/A"}
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-dashboardText text-sm">{t("orderModal.noAddress")}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Order Meta Information */}
            <div className="border border-dashboardStroke rounded-lg overflow-hidden">
              <div className="bg-dashboardBg px-4 py-3 border-b border-dashboardStroke">
                <h3 className="font-semibold text-dashboardDarkText">{t("orderModal.meta")}</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.orderId")}</p>
                  <p className="text-dashboardDarkText font-medium text-sm break-all">{order._id}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.customOrderId")}</p>
                  <p className="text-dashboardDarkText font-medium">{order.customOrderId || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.invoiceId")}</p>
                  <p className="text-dashboardDarkText font-medium">{order.invoiceId || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.language")}</p>
                  <p className="text-dashboardDarkText font-medium uppercase">{order.language || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.multiSeller")}</p>
                  <p className="text-dashboardDarkText font-medium">{order.isMultiSellerOrder ? t("orderModal.yes") : t("orderModal.no")}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.notificationOption")}</p>
                  <p className="text-dashboardDarkText font-medium">{order.notificationOption || "N/A"}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.createdAt")}</p>
                  <p className="text-dashboardDarkText font-medium">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.updatedAt")}</p>
                  <p className="text-dashboardDarkText font-medium">{formatDate(order.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-dashboardText text-sm">{t("orderModal.expiryDate")}</p>
                  <p className="text-dashboardDarkText font-medium">{formatDate(order.expiryDate)}</p>
                </div>
              </div>
              {order.invoiceUrl && (
                <div className="px-4 pb-4">
                  <p className="text-dashboardText text-sm mb-2">{t("orderModal.invoiceUrl")}</p>
                  <a
                    href={order.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-themeOrange hover:underline text-sm break-all"
                  >
                    {order.invoiceUrl}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-dashboardStroke sticky bottom-0">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-dashboardDark hover:bg-dashboardDark/90 text-white rounded-md font-medium transition-colors"
              >
                {t("orderModal.close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
